import { NcFileBatchEventType } from '@v2/pages/common/enums';
import i18n from 'i18n-js';
import { RawMachineEventData } from '@interfaces/base';
import {
  FormattedDataItem,
  NCFileBatchEvent,
  NcFileTimesConfig,
  ParsedMachineEventData,
  ProductivityStatusTimes,
  TimeCategoryValuesWithMaxTime,
  UseEventBarChartParams,
} from '../types';
import { ProductionBatchStatistics } from '@interfaces/search';
import { ClampingDetails } from '@v2/context/Times/page/interfaces';
import { DEFAULT_EVENT_KEY_FIELDS } from '@v2/pages/common/components/NCFileBatchEvents/constants';
import { deepClone } from '@helpers';
import { BULLET } from '@v2/pages/common/constants/base.constants';

/**
 * Gets the appropriate row key based on event type
 * @param eventType - The type of event (SETUP, CYCLE, ACTIVE, CHANGE, TRACE)
 * @param event - The event object containing potential key fields
 * @returns The row key to use for accordion state management
 */
export const getRowKey = (eventType: NcFileBatchEventType, event: any): string | number => {
  if (eventType === NcFileBatchEventType.SETUP) {
    return `${event?.productionBatchId}-${event?.startedOn}`;
  }

  // For all other event types (CYCLE, ACTIVE, CHANGE, TRACE)
  return `${event?.cycleNumber}-${event?.startedOn}`;
};

export const getStartBlockText = (text: string | null) => {
  if (!text) return '';

  if (text.toLowerCase() === 'null') {
    return i18n.t('Not Available');
  }

  return text;
};

/**
 * Parses raw machine event data by removing unnecessary fields
 * @param rawData - The raw machine event data from the backend
 * @returns Parsed machine event data with only necessary fields
 */
export const parseRawMachineEventData = (rawData: RawMachineEventData | null): ParsedMachineEventData | null => {
  if (!rawData) return null;

  // Create a deep copy of the raw data
  const dataCopy = deepClone(rawData) as RawMachineEventData;

  // Remove top-level fields
  const {
    $type,
    machineId,
    sequenceNumber,
    subStatus,
    executionMode,
    doIgnoreMachineStatusVerification,
    sentFromBackend,
    sentFromCache,
    timestamp,
    tryCount,
    ...restData
  } = dataCopy;

  // Process machineCountingDetails - remove countingMethod
  const { countingMethod, ...restMachineCountingDetails } = restData.machineCountingDetails ?? {};

  // Process ncFiles - remove $type, contentHash, and transferStateId
  const processedNcFiles = (restData.ncFiles ?? []).map((ncFile) => {
    const { $type: ncFileType, transferState, ...restNcFile } = ncFile ?? {};
    const { transferStateId, ...restTransferState } = transferState ?? {};

    // Remove contentHash if it exists
    const ncFileWithoutContentHash = { ...restNcFile } as any;
    delete ncFileWithoutContentHash.contentHash;

    return {
      ...ncFileWithoutContentHash,
      transferState: restTransferState,
    };
  });

  return {
    ...restData,
    machineCountingDetails: restMachineCountingDetails,
    ncFiles: processedNcFiles,
  };
};

/**
 * Formats parsed machine event data into a readable structure
 * @param data - The parsed machine event data
 * @param indent - Current indentation level (for nested objects)
 * @returns Array of formatted data items for display
 */
export const formatMachineEventDataForDisplay = (data: ParsedMachineEventData | null, indent: number = 0): FormattedDataItem[] => {
  if (!data) return [];

  const formatValue = (key: string, value: any, currentIndent: number, showBullet: boolean = true): FormattedDataItem => {
    // Handle null or undefined
    if (value === null || value === undefined) {
      return {
        key: showBullet ? `${BULLET} ${key}` : key,
        value: value === null ? 'null' : 'undefined',
        type: 'primitive',
        indent: currentIndent,
      };
    }

    // Handle arrays
    if (Array.isArray(value)) {
      const arrayItems = value
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            // For objects in array, show their properties as nested bullets
            return Object.entries(item).map(([itemKey, itemValue]) => formatValue(itemKey, itemValue, currentIndent + 1, true));
          }
          return formatValue('', item, currentIndent + 1, true);
        })
        .flat();

      return {
        key: showBullet ? `${BULLET} ${key}` : key,
        value: arrayItems,
        type: 'array',
        indent: currentIndent,
      };
    }

    // Handle objects
    if (typeof value === 'object') {
      const objectItems = Object.entries(value).map(([objKey, objValue]) => formatValue(objKey, objValue, currentIndent + 1, true));

      return {
        key: showBullet ? `${BULLET} ${key}` : key,
        value: objectItems,
        type: 'object',
        indent: currentIndent,
      };
    }

    // Handle primitive values (string, number, boolean)
    return {
      key: showBullet ? `${BULLET} ${key}` : key,
      value: String(value),
      type: 'primitive',
      indent: currentIndent,
    };
  };

  const formattedData: FormattedDataItem[] = [];

  Object.entries(data).forEach(([key, value]) => {
    formattedData.push(formatValue(key, value, indent, true));
  });

  return formattedData;
};

export const getNCFileStatisticIdealTime = (config: NcFileTimesConfig, key: keyof NcFileTimesConfig) =>
  config?.[key]?.manualIdeal ? config[key]?.ideal : config[key]?.idealAutomatic;

export const getNCFileStatisticScheduledTime = (config: NcFileTimesConfig, key: keyof NcFileTimesConfig) =>
  config?.[key]?.manualScheduled ? config[key]?.scheduled : config[key]?.scheduledAutomatic;

export const getNCFileStatisticPaidTime = (config: NcFileTimesConfig, key: keyof NcFileTimesConfig) =>
  config?.[key]?.manualPaid ? config[key]?.paid : config[key]?.paidAutomatic;

export const getNcFileTimeValues = (
  ncFileTimesConfig: NcFileTimesConfig,
  ncFileStatisticsOverall: UseEventBarChartParams['ncFileStatistics'],
  eventType: NcFileBatchEventType
): TimeCategoryValuesWithMaxTime => {
  const statisticsOverall = ncFileStatisticsOverall?.statisticsOverall;

  switch (eventType) {
    case NcFileBatchEventType.CYCLE: {
      const idealActiveTimeInSec = getNCFileStatisticIdealTime(ncFileTimesConfig, 'active') || 0;
      const idealChangeTimeInSec = getNCFileStatisticIdealTime(ncFileTimesConfig, 'change') || 0;
      const idealTimeInSec = idealActiveTimeInSec + idealChangeTimeInSec;

      const actualActiveTimeInSec = statisticsOverall?.averageActiveSecs || 0;
      const actualChangeTimeInSec = statisticsOverall?.averageChangeSecs || 0;
      const actualTimeInSec = actualActiveTimeInSec + actualChangeTimeInSec;

      const scheduledActiveTimeInSec = getNCFileStatisticScheduledTime(ncFileTimesConfig, 'active') || 0;
      const scheduledChangeTimeInSec = getNCFileStatisticScheduledTime(ncFileTimesConfig, 'change') || 0;
      const scheduledTimeInSec = scheduledActiveTimeInSec + scheduledChangeTimeInSec;

      const paidActiveTimeInSec = getNCFileStatisticPaidTime(ncFileTimesConfig, 'active') || 0;
      const paidChangeTimeInSec = getNCFileStatisticPaidTime(ncFileTimesConfig, 'change') || 0;
      const paidTimeInSec = paidActiveTimeInSec + paidChangeTimeInSec;

      return {
        idealActiveTimeInSec,
        idealChangeTimeInSec,
        idealTimeInSec,

        actualActiveTimeInSec,
        actualChangeTimeInSec,
        actualTimeInSec,

        scheduledActiveTimeInSec,
        scheduledChangeTimeInSec,
        scheduledTimeInSec,

        paidTimeInSec,
        paidActiveTimeInSec,
        paidChangeTimeInSec,

        maxTimeInSec: statisticsOverall?.maxCycleTimePerCycleInSeconds || 0,
      };
    }
    case NcFileBatchEventType.ACTIVE:
      return {
        idealTimeInSec: getNCFileStatisticIdealTime(ncFileTimesConfig, 'active') || 0,
        paidTimeInSec: getNCFileStatisticPaidTime(ncFileTimesConfig, 'active') || 0,
        actualTimeInSec: statisticsOverall?.averageActiveSecs || 0,
        scheduledTimeInSec: getNCFileStatisticScheduledTime(ncFileTimesConfig, 'active') || 0,
        maxTimeInSec: statisticsOverall?.maxActiveTimePerCycleInSeconds || 0,
      };
    case NcFileBatchEventType.CHANGE:
      return {
        idealTimeInSec: getNCFileStatisticIdealTime(ncFileTimesConfig, 'change') || 0,
        paidTimeInSec: getNCFileStatisticPaidTime(ncFileTimesConfig, 'change') || 0,
        actualTimeInSec: statisticsOverall?.averageChangeSecs || 0,
        scheduledTimeInSec: getNCFileStatisticScheduledTime(ncFileTimesConfig, 'change') || 0,
        maxTimeInSec: statisticsOverall?.maxChangeTimePerCycleInSeconds || 0,
      };
    case NcFileBatchEventType.SETUP: {
      const idealTimeInSec = getNCFileStatisticIdealTime(ncFileTimesConfig, 'setup') || 0;
      const paidTimeInSec = getNCFileStatisticPaidTime(ncFileTimesConfig, 'setup') || 0;
      const actualTimeInSec = statisticsOverall?.averageSetupSecs || 0;
      const scheduledTimeInSec = getNCFileStatisticScheduledTime(ncFileTimesConfig, 'setup') || 0;
      return {
        idealTimeInSec,
        paidTimeInSec,
        actualTimeInSec,
        scheduledTimeInSec,
        maxTimeInSec: Math.max(idealTimeInSec, paidTimeInSec, actualTimeInSec, scheduledTimeInSec),
      };
    }
    default:
      return { idealTimeInSec: 0, paidTimeInSec: 0, scheduledTimeInSec: 0, actualTimeInSec: 0, maxTimeInSec: 0 };
  }
};

export const getBatchTimeValues = (batchData: ProductionBatchStatistics | null, eventType: NcFileBatchEventType): TimeCategoryValuesWithMaxTime => {
  if (!batchData) return { idealTimeInSec: 0, paidTimeInSec: 0, scheduledTimeInSec: 0, actualTimeInSec: 0, maxTimeInSec: 0 };

  switch (eventType) {
    case NcFileBatchEventType.CYCLE:
      return {
        idealActiveTimeInSec: batchData.idealActiveTimeAverageInSeconds || 0,
        idealChangeTimeInSec: batchData.idealChangeTimeAverageInSeconds || 0,
        idealTimeInSec: batchData.idealCycleTimeAverageInSeconds || 0,

        actualActiveTimeInSec: batchData.actualActiveTimeAverageInSeconds || 0,
        actualChangeTimeInSec: batchData.actualChangeTimeAverageInSeconds || 0,
        actualTimeInSec: batchData.actualCycleTimeAverageInSeconds || 0,

        scheduledActiveTimeInSec: batchData.scheduledActiveTimeAverageInSeconds || 0,
        scheduledChangeTimeInSec: batchData.scheduledChangeTimeAverageInSeconds || 0,
        scheduledTimeInSec: batchData.scheduledCycleTimeAverageInSeconds || 0,

        paidTimeInSec: batchData.paidCycleTimeAverageInSeconds || 0,
        paidActiveTimeInSec: batchData.paidActiveTimeAverageInSeconds || 0,
        paidChangeTimeInSec: batchData.paidChangeTimeAverageInSeconds || 0,

        maxTimeInSec: batchData.maxCycleTimePerCycleInSeconds || 0,
      };
    case NcFileBatchEventType.ACTIVE:
      return {
        idealTimeInSec: batchData.idealActiveTimeAverageInSeconds || 0,
        paidTimeInSec: batchData.paidActiveTimeAverageInSeconds || 0,
        actualTimeInSec: batchData.actualActiveTimeAverageInSeconds || 0,
        scheduledTimeInSec: batchData.scheduledActiveTimeAverageInSeconds || 0,
        maxTimeInSec: batchData.maxActiveTimePerCycleInSeconds || 0,
      };
    case NcFileBatchEventType.CHANGE:
      return {
        idealTimeInSec: batchData.idealChangeTimeAverageInSeconds || 0,
        paidTimeInSec: batchData.paidChangeTimeAverageInSeconds || 0,
        actualTimeInSec: batchData.actualChangeTimeAverageInSeconds || 0,
        scheduledTimeInSec: batchData.scheduledChangeTimeAverageInSeconds || 0,
        maxTimeInSec: batchData.maxChangeTimePerCycleInSeconds || 0,
      };
    case NcFileBatchEventType.SETUP: {
      const idealTimeInSec = batchData.idealSetupTimeInSeconds || 0;
      const paidTimeInSec = batchData.paidSetupTimeInSeconds || 0;
      const actualTimeInSec = batchData.actualSetupTimeInSeconds || 0;
      const scheduledTimeInSec = batchData.scheduledSetupTimeInSeconds || 0;
      return {
        idealTimeInSec,
        paidTimeInSec,
        actualTimeInSec,
        scheduledTimeInSec,
        maxTimeInSec: Math.max(idealTimeInSec, paidTimeInSec, actualTimeInSec, scheduledTimeInSec),
      };
    }
    default:
      return { idealTimeInSec: 0, paidTimeInSec: 0, scheduledTimeInSec: 0, actualTimeInSec: 0, maxTimeInSec: 0 };
  }
};

export const getEventDurations = (
  event: UseEventBarChartParams['events'][number],
  eventType: NcFileBatchEventType
): Pick<ProductivityStatusTimes, 'activeTimeInSec' | 'changeTimeInSec' | 'setupTimeInSec'> => {
  switch (eventType) {
    case NcFileBatchEventType.CYCLE:
      return {
        activeTimeInSec: event.durationActiveInSeconds || 0,
        changeTimeInSec: event.durationChangeInSeconds || 0,
        setupTimeInSec: 0,
      };
    case NcFileBatchEventType.ACTIVE:
      return { activeTimeInSec: event.durationActiveInSeconds || 0, changeTimeInSec: 0, setupTimeInSec: 0 };
    case NcFileBatchEventType.CHANGE:
      return { activeTimeInSec: 0, changeTimeInSec: event.durationChangeInSeconds || 0, setupTimeInSec: 0 };
    case NcFileBatchEventType.SETUP:
      return { activeTimeInSec: 0, changeTimeInSec: 0, setupTimeInSec: event.actualSetupTimeInSeconds || 0 };
    default:
      return { activeTimeInSec: 0, changeTimeInSec: 0, setupTimeInSec: 0 };
  }
};

/**
 * Maps ClampingDetails fields to ProductionBatchStatistics fields for optimistic updates
 * ClampingDetails uses "runTime" terminology while ProductionBatchStatistics uses "activeTime"
 */
export const mapClampingDetailsToBatchStatistics = (
  updatedFields: Partial<ClampingDetails>,
  currentBatchData: ProductionBatchStatistics
): Partial<ProductionBatchStatistics> => {
  const batchUpdates: Partial<ProductionBatchStatistics> = {};

  // Ideal times mapping
  if (updatedFields.runTimePerPcsSecond !== undefined) {
    batchUpdates.idealActiveTimeAverageInSeconds = updatedFields.runTimePerPcsSecond ?? 0;
  }
  if (updatedFields.changeTimePerPcsSecond !== undefined) {
    batchUpdates.idealChangeTimeAverageInSeconds = updatedFields.changeTimePerPcsSecond ?? 0;
  }
  if (updatedFields.setupTimeSecond !== undefined) {
    batchUpdates.idealSetupTimeInSeconds = updatedFields.setupTimeSecond ?? 0;
  }

  // Scheduled times mapping
  if (updatedFields.scheduledRunTimeInSeconds !== undefined) {
    batchUpdates.scheduledActiveTimeAverageInSeconds = updatedFields.scheduledRunTimeInSeconds ?? 0;
  }
  if (updatedFields.scheduledChangeTimeInSeconds !== undefined) {
    batchUpdates.scheduledChangeTimeAverageInSeconds = updatedFields.scheduledChangeTimeInSeconds ?? 0;
  }
  if (updatedFields.scheduledSetupTimeInSeconds !== undefined) {
    batchUpdates.scheduledSetupTimeInSeconds = updatedFields.scheduledSetupTimeInSeconds ?? 0;
  }

  // Paid times mapping
  if (updatedFields.paidRunTimePerPcsSecond !== undefined) {
    batchUpdates.paidActiveTimeAverageInSeconds = updatedFields.paidRunTimePerPcsSecond;
  }
  if (updatedFields.paidChangeTimePerPcsSecond !== undefined) {
    batchUpdates.paidChangeTimeAverageInSeconds = updatedFields.paidChangeTimePerPcsSecond;
  }
  if (updatedFields.paidSetupTimeSecond !== undefined) {
    batchUpdates.paidSetupTimeInSeconds = updatedFields.paidSetupTimeSecond;
  }

  // Manual flags mapping
  if (updatedFields.useManualIdealRunTime !== undefined) {
    batchUpdates.idealActiveTimeSetManually = updatedFields.useManualIdealRunTime;
  }
  if (updatedFields.useManualIdealChangeTime !== undefined) {
    batchUpdates.idealChangeTimeSetManually = updatedFields.useManualIdealChangeTime;
  }
  if (updatedFields.useManualIdealSetupTime !== undefined) {
    batchUpdates.idealSetupTimeSetManually = updatedFields.useManualIdealSetupTime;
  }
  if (updatedFields.useManualScheduledRunTime !== undefined) {
    batchUpdates.scheduledActiveTimeSetManually = updatedFields.useManualScheduledRunTime;
  }
  if (updatedFields.useManualScheduledChangeTime !== undefined) {
    batchUpdates.scheduledChangeTimeSetManually = updatedFields.useManualScheduledChangeTime;
  }
  if (updatedFields.useManualScheduledSetupTime !== undefined) {
    batchUpdates.scheduledSetupTimeSetManually = updatedFields.useManualScheduledSetupTime;
  }

  // Calculate cycle time averages using current batch data as fallback
  const idealActive = batchUpdates.idealActiveTimeAverageInSeconds ?? currentBatchData.idealActiveTimeAverageInSeconds ?? 0;
  const idealChange = batchUpdates.idealChangeTimeAverageInSeconds ?? currentBatchData.idealChangeTimeAverageInSeconds ?? 0;
  if (batchUpdates.idealActiveTimeAverageInSeconds !== undefined || batchUpdates.idealChangeTimeAverageInSeconds !== undefined) {
    batchUpdates.idealCycleTimeAverageInSeconds = idealActive + idealChange;
  }

  const scheduledActive = batchUpdates.scheduledActiveTimeAverageInSeconds ?? currentBatchData.scheduledActiveTimeAverageInSeconds ?? 0;
  const scheduledChange = batchUpdates.scheduledChangeTimeAverageInSeconds ?? currentBatchData.scheduledChangeTimeAverageInSeconds ?? 0;
  if (batchUpdates.scheduledActiveTimeAverageInSeconds !== undefined || batchUpdates.scheduledChangeTimeAverageInSeconds !== undefined) {
    batchUpdates.scheduledCycleTimeAverageInSeconds = scheduledActive + scheduledChange;
  }

  const paidActive = batchUpdates.paidActiveTimeAverageInSeconds ?? currentBatchData.paidActiveTimeAverageInSeconds ?? 0;
  const paidChange = batchUpdates.paidChangeTimeAverageInSeconds ?? currentBatchData.paidChangeTimeAverageInSeconds ?? 0;
  if (batchUpdates.paidActiveTimeAverageInSeconds !== undefined || batchUpdates.paidChangeTimeAverageInSeconds !== undefined) {
    batchUpdates.paidCycleTimeAverageInSeconds = paidActive + paidChange;
  }

  return batchUpdates;
};

export const getEventKeyValue = (event: NCFileBatchEvent | null): number | null => {
  if (!event) return null;
  for (const field of DEFAULT_EVENT_KEY_FIELDS) {
    const value = event[field as keyof typeof event];
    if (value != null) return value as number;
  }
  return null;
};
